import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useDeviceDetection } from '../hooks/useDeviceDetection';
import { UI_TEXT } from '../config/gameConfig';

const MenuScreen = ({ onStartGame }) => {
  const { isMobile } = useDeviceDetection();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    }

    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#0a0a0a] text-white font-mono relative overflow-hidden py-8">
      {/* Background Grid */}
      <div
        className="absolute inset-0 opacity-10 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(#333 1px, transparent 1px), linear-gradient(90deg, #333 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }}
      />

      <motion.div
        className={`z-10 text-center space-y-8 w-full ${isMobile ? 'px-4 max-w-sm' : 'px-4 max-w-4xl'}`}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Title Section */}
        <div className="space-y-2">
          <motion.div
            className={`${isMobile ? 'text-xs' : 'text-sm'} text-[#FFD900] tracking-[0.5em] uppercase`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            {UI_TEXT.menu.systemInitialized}
          </motion.div>

          <motion.h1
            className={`${isMobile ? 'text-4xl' : 'text-6xl md:text-8xl'} font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-gray-500 tracking-tighter`}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            {UI_TEXT.menu.title}
            <br />
            <span className="text-[#FFD900]">{UI_TEXT.menu.subtitle}</span>
          </motion.h1>

          <motion.div
            className={`h-1 ${isMobile ? 'w-16' : 'w-24'} bg-[#FFD900] mx-auto mt-6`}
            initial={{ width: 0 }}
            animate={{ width: isMobile ? 64 : 96 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          />
        </div>

        {/* Album Comment */}
        <motion.div
          className="bg-[#FFD900]/10 border border-[#FFD900]/30 rounded-lg p-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <p className="text-[#FFD900] text-sm leading-relaxed">
            {UI_TEXT.menu.albumComment}
          </p>
        </motion.div>

        {/* Start Button */}
        <motion.button
          onClick={onStartGame}
          className={`group relative inline-flex items-center justify-center ${isMobile ? 'px-8 py-3 text-base' : 'px-12 py-4 text-lg'} overflow-hidden font-bold text-black transition-all duration-300 bg-[#FFD900] hover:bg-[#ffed4e] focus:outline-none shadow-lg hover:shadow-[#FFD900]/50`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <span className="relative tracking-widest uppercase">{UI_TEXT.menu.startButton}</span>
        </motion.button>

        {/* Install Button */}
        {showInstallButton && isMobile && (
          <motion.button
            onClick={handleInstallClick}
            className="px-8 py-3 bg-gradient-to-r from-green-600 to-green-500 text-white font-bold uppercase tracking-wider rounded-lg shadow-lg hover:shadow-green-500/50 transition-all"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {UI_TEXT.menu.installButton}
          </motion.button>
        )}

        <motion.div
          className={`text-gray-600 ${isMobile ? 'text-xs' : 'text-xs'} tracking-widest uppercase`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
        >
          {UI_TEXT.menu.version}
        </motion.div>
      </motion.div>
    </div>
  );
};

export default MenuScreen;